import React, { useEffect, useMemo, useState } from "react";
import { IdentityFinanceInputsProps } from "@/screen/Identities/IdentityFinanceInputs/Interfaces/IdentityFinanceInputsInterfaces";
import { PendingFieldShell } from "@/screen/Identities/Styles/IdentityFormStyles";
import { FormSection, FormRow, FormField, FormLabel, FormInput, FormSelect, QuestionText, RadioGroup, RadioLabel, RadioInput, DocumentPlaceholder, ProofOfIncomePlaceholderTitle, ProofOfIncomeOptionsList, FileFormatHint, FormFieldFull, FormFieldHalf, TallDocumentPreview, DisclaimerBox } from "@/screen/Identities/IdentityFinanceInputs/Styles/IdentityFinanceInputsStyles";
import { getKycLocalBlocks } from "@/screen/Identities/IdentityForm/identityKycRules";
import IdentityAttachmentPreview from "@/screen/Identities/components/IdentityAttachmentPreview";

const ECONOMIC_ACTIVITY_OPTIONS =
[
    { value: "SELF_EMPLOYED", label: "Autónomo" },
    { value: "OTHER_EMPLOYEES", label: "Otros asalariados" }
];

const GAFI_QUESTION_TEXT =
    "¿Alguno de los intervinientes o sus familiares realiza alguna actividad " +
    "o es residente/nacional de alguna de las jurisdicciones consideradas " +
    "de riesgo de acuerdo con la normativa de prevención de blanqueo de " +
    "capitales o no cooperantes según GAFI (Afganistán, Corea del Norte, " +
    "Irán, Laos, Papúa Nueva Guinea, Uganda, Yemen, Bosnia y Herzegovina, " +
    "Guyana, Irak, Birmania (Myanmar), Siria y Vanuatu)?";

const PUBLIC_POSITION_QUESTION_TEXT =
    "¿Alguno de los intervinientes o sus familiares son personas políticamente " +
    "expuestas (personas que desempeñan o han desempeñado funciones " +
    "públicas relevantes en los últimos años)?";

const PLACEHOLDER_PROOF_OF_INCOME = "Justificante de ingresos";

const PROOF_OF_INCOME_HELP_TEXT = "Debes subir uno de los siguientes documentos:";
const PROOF_OF_INCOME_OPTIONS =
[
    "Declaración de la Renta Completa.",
    "Nómina actual (mes inmediatamente anterior).",
    "Certificado de Retenciones de la Empresa.",
    "Pensión: carta de la Seguridad Social donde se indica la concesión y el importe percibido."
];

const IdentityFinanceInputs: React.FC<IdentityFinanceInputsProps> = ({formData, validation, highlightPendingManualFields, isFormLocked, onChange, onProofOfIncomeChange, onProofOfIncomeResolvedFile, onProofOfIncomeErrors}) =>
{
    const ph = (ok: boolean): boolean => highlightPendingManualFields && !ok;
    const [proofOfIncomeAttachmentError, setProofOfIncomeAttachmentError] = useState<string | null>(null);

    // El bloqueo por persona con responsabilidad pública se calcula con las reglas KYC para no
    // duplicar la condición; el controller lo vuelve a evaluar sobre formData para invalidar el envío.
    const publicPositionBlockMessage = useMemo(() =>
        getKycLocalBlocks(formData).find((block) => block.code === "KYC_BLOCKED_PUBLIC_POSITION")?.message ?? null,
    [formData]);

    const proofOfIncomeSource = formData.proofOfIncome instanceof File || typeof formData.proofOfIncome === "string"
        ? formData.proofOfIncome
        : null;

    const proofOfIncomePlaceholder =
    (
        <DocumentPlaceholder>
            <ProofOfIncomePlaceholderTitle>{PROOF_OF_INCOME_HELP_TEXT}</ProofOfIncomePlaceholderTitle>
            <ProofOfIncomeOptionsList>
                {PROOF_OF_INCOME_OPTIONS.map((option, index) =>
                (
                    <li key={index}>{option}</li>
                ))}
            </ProofOfIncomeOptionsList>
        </DocumentPlaceholder>
    );

    useEffect(() =>
    {
        if(!onProofOfIncomeErrors)
        {
            return;
        }

        if(proofOfIncomeAttachmentError)
        {
            onProofOfIncomeErrors([{field: "Documento. Prueba de Ingresos", message: proofOfIncomeAttachmentError}]);
            return;
        }

        onProofOfIncomeErrors([]);
    }, [proofOfIncomeAttachmentError, onProofOfIncomeErrors]);

    return(
        <FormSection>
            <h3>Información Financiera</h3>
            <FormRow>
                <FormField>
                    <FormLabel required>Actividad</FormLabel>
                    <FormSelect name="economicActivity" value={formData.economicActivity || ""} onChange={onChange} disabled={isFormLocked}
                        $pendingHighlight={ph(validation.economicActivity)}
                    >
                        <option value="">-- Seleccionar --</option>
                        {ECONOMIC_ACTIVITY_OPTIONS.map((opt) =>
                        (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </FormSelect>
                </FormField>
                <FormField>
                    <FormLabel required>Justificante de ingresos</FormLabel>
                    <FormInput type="file" id="proofOfIncome" name="proofOfIncome"
                        accept=".png,.jpg,.pdf,.jpeg" disabled={isFormLocked} autoComplete="off"
                        onChange={(e) =>
                        {
                            const file = e.target.files?.[0] || null;
                            // El atributo accept es solo un filtro del diálogo: el usuario puede colar otros
                            // formatos (Word, etc.) con "Todos los archivos" o arrastrando. Validamos aquí la
                            // extensión antes de enviar, porque Document Intelligence rechaza cualquier cosa
                            // que no sea imagen o PDF y el guardado acababa en un error genérico sin salida.
                            if(file)
                            {
                                const allowedExtensions = ["png", "jpg", "jpeg", "pdf"];
                                const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
                                if(!allowedExtensions.includes(extension))
                                {
                                    setProofOfIncomeAttachmentError("El formato del justificante no es válido. Sube un archivo PNG, JPG o PDF (no se admiten documentos de Word).");
                                    onProofOfIncomeChange(null);
                                    e.target.value = "";
                                    return;
                                }
                            }
                            setProofOfIncomeAttachmentError(null);
                            onProofOfIncomeChange(file);
                        }}
                        $pendingHighlight={ph(validation.proofOfIncome)}
                    />
                    <FileFormatHint>* Formatos admitidos: .png, .jpg, .jpeg y .pdf</FileFormatHint>
                </FormField>
            </FormRow>
            <FormRow>
                <FormFieldFull>
                    <TallDocumentPreview>
                        <IdentityAttachmentPreview
                            source={proofOfIncomeSource}
                            alt={PLACEHOLDER_PROOF_OF_INCOME}
                            placeholder={proofOfIncomePlaceholder}
                            onResolvedFile={onProofOfIncomeResolvedFile}
                            onErrorChange={setProofOfIncomeAttachmentError}
                        />
                    </TallDocumentPreview>
                </FormFieldFull>
            </FormRow>
            <FormRow>
                <FormFieldHalf>
                    <QuestionText>{GAFI_QUESTION_TEXT}</QuestionText>
                    <PendingFieldShell $pendingHighlight={ph(validation.gafiQuestion)}>
                        <RadioGroup>
                            <RadioLabel>
                                <RadioInput type="radio" name="gafiQuestion" value="true" disabled={isFormLocked}
                                    onChange={onChange} checked={formData.gafiQuestion === true}
                                />
                                Si
                            </RadioLabel>
                            <RadioLabel>
                                <RadioInput type="radio" name="gafiQuestion" value="false" disabled={isFormLocked}
                                    onChange={onChange} checked={formData.gafiQuestion === false}
                                />
                                No
                            </RadioLabel>
                        </RadioGroup>
                    </PendingFieldShell>
                </FormFieldHalf>
                <FormFieldHalf>
                    <QuestionText>{PUBLIC_POSITION_QUESTION_TEXT}</QuestionText>
                    <PendingFieldShell $pendingHighlight={ph(validation.publicPosition)}>
                        <RadioGroup>
                            <RadioLabel>
                                <RadioInput type="radio" name="publicPosition" value="true" disabled={isFormLocked}
                                    onChange={onChange} checked={formData.publicPosition === true}
                                />
                                Si
                            </RadioLabel>
                            <RadioLabel>
                                <RadioInput type="radio" name="publicPosition" value="false" disabled={isFormLocked}
                                    onChange={onChange} checked={formData.publicPosition === false}
                                />
                                No
                            </RadioLabel>
                        </RadioGroup>
                    </PendingFieldShell>
                    {publicPositionBlockMessage &&
                    (
                        <DisclaimerBox>
                            <p>{publicPositionBlockMessage}</p>
                        </DisclaimerBox>
                    )}
                </FormFieldHalf>
            </FormRow>
        </FormSection>
    );
};

export default IdentityFinanceInputs;